import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, ArrowRight, X, UserPlus, User } from "lucide-react";
import { useApp, API } from "../context";

const SNOOZE_KEY = "iw_add_explorer_tip_snoozed_until_";
const COOLDOWN_MS = 24 * 60 * 60 * 1000; // re-appears every 24h until an Explorer is added

/**
 * AddExplorerTip, companion card to <CompleteProfileTip>. For grown-up accounts
 * (parent / solo, anyone who is NOT a child explorer) who have NOT yet added an
 * Explorer, it points to Parent Corner to add one. Once an Explorer exists (or the
 * card is dismissed) it disappears. Parent Corner is clearly named as the place to
 * add AND edit explorers in future.
 */
const AddExplorerTip = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [hasExplorer, setHasExplorer] = useState(null); // null = still loading

  useEffect(() => {
    if (!user) return;
    try {
      const until = Number(localStorage.getItem(`${SNOOZE_KEY}${user.id}`) || 0);
      if (Date.now() < until) setDismissed(true);
    } catch (_) { /* private mode */ }
  }, [user]);

  // Check whether this account already has an Explorer (child), if so, hide.
  useEffect(() => {
    let alive = true;
    if (!user || user.role === "child_explorer") { setHasExplorer(true); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/profile/children/${user.id}`);
        const list = Array.isArray(res.data) ? res.data : (res.data?.children || []);
        if (alive) setHasExplorer(list.length > 0);
      } catch (_) {
        // On error, do NOT nudge — avoids falsely showing "Add your Explorer"
        // to established accounts whose lookup briefly fails on refresh.
        if (alive) setHasExplorer(true);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  if (!user) return null;
  if (user.role === "child_explorer") return null; // kids don't add explorers
  if (dismissed) return null;
  if (hasExplorer !== false) return null; // hide while loading or if they already have one

  const dismiss = () => {
    setDismissed(true);
    // Snooze for 24h rather than forever, the CTA re-appears daily until the
    // parent actually adds an Explorer (at which point hasExplorer hides it).
    try { localStorage.setItem(`${SNOOZE_KEY}${user.id}`, String(Date.now() + COOLDOWN_MS)); } catch (_) {}
  };

  return (
    <div
      className="relative rounded-2xl p-4 sm:p-5 border-2 overflow-hidden animate-fade-in"
      style={{
        background: "linear-gradient(135deg, rgba(16,185,129,0.18), rgba(6,182,212,0.14), rgba(59,130,246,0.10))",
        borderColor: "rgba(52,211,153,0.4)",
        boxShadow: "0 0 32px rgba(16,185,129,0.16)",
      }}
      data-testid="add-explorer-tip"
    >
      <div className="absolute -top-6 -right-6 opacity-20 pointer-events-none">
        <Users size={120} className="text-emerald-300 animate-pulse" />
      </div>

      <button
        onClick={dismiss}
        className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/60 hover:text-white transition-colors"
        aria-label="Dismiss tip"
        data-testid="add-explorer-tip-close"
      >
        <X size={14} />
      </button>

      <div className="relative flex items-start gap-3 sm:gap-4 pr-8">
        <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #10B981, #06B6D4)" }}>
          <UserPlus size={22} className="text-white" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2 py-0.5 text-[9px] font-bold tracking-wider uppercase rounded-full bg-emerald-400/20 text-emerald-200 border border-emerald-400/40">
              Parent Corner
            </span>
          </div>
          <h3 className="text-sm sm:text-base font-fredoka font-bold text-white mb-1" data-testid="add-explorer-tip-headline">
            Add your Explorer
          </h3>
          <div className="flex items-center gap-2 mb-2" data-testid="add-explorer-preview" aria-hidden="true">
            {["from-pink-400 to-rose-500", "from-amber-400 to-orange-500", "from-sky-400 to-blue-500"].map((g, k) => (
              <div key={k} className={`w-8 h-8 rounded-full bg-gradient-to-br ${g} border-2 border-slate-900 flex items-center justify-center ${k > 0 ? "-ml-3" : ""}`} style={{ opacity: 0.55 - k * 0.12 }}>
                <User size={15} className="text-white/90" />
              </div>
            ))}
            <div className="-ml-3 w-8 h-8 rounded-full border-2 border-dashed border-emerald-300/70 flex items-center justify-center bg-emerald-400/10">
              <UserPlus size={15} className="text-emerald-200" />
            </div>
            <span className="text-[11px] text-white/45 font-semibold ml-1">Your Explorer goes here</span>
          </div>
          <p className="text-xs sm:text-sm text-white/70 leading-relaxed mb-3">
            Add your child as an Explorer to unlock their full learning experience, then switch between
            <span className="text-white/90 font-bold"> Learn as me</span> and
            <span className="text-white/90 font-bold"> Learn as them</span> anytime.
            <span className="block text-white/50 mt-1">The <span className="font-bold text-emerald-200/90">Parent Corner</span> is where you add and edit Explorers.</span>
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => navigate("/parent-corner?tab=children&add=1")}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.04]"
              style={{ background: "linear-gradient(135deg, #10B981, #06B6D4)", boxShadow: "0 4px 16px rgba(16,185,129,0.35)" }}
              data-testid="add-explorer-tip-cta"
            >
              Add an Explorer in Parent Corner
              <ArrowRight size={14} />
            </button>
            <button
              onClick={dismiss}
              className="text-xs text-white/40 hover:text-white/70 px-2 py-1 transition-colors"
              data-testid="add-explorer-tip-later"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddExplorerTip;
